import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/dealership/lib/api";
import { Panel, Chip } from "@/components/lux";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { paymentFor, money } from "@/dealership/lib/desk";
import { Grid3x3, Plus, X, Pencil, Check } from "lucide-react";

const FREQ_TITLE = { monthly: "Monthly", weekly: "Weekly", semimonthly: "Semi-monthly", biweekly: "Bi-weekly" };

/** The DeskIt-style matrix: term rows x (down payment | rate) columns. */
export default function PaymentGrid({ ws, settings, onApply }) {
  const qc = useQueryClient();
  const grid = settings?.grid || {};
  const [mode, setMode] = useState(grid.mode || "down");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const fin = ws.finance || {};

  const live = editing && draft ? draft : {
    terms: grid.terms?.length ? grid.terms : [36, 48, 60, 72, 84, 96],
    downs: grid.downs?.length ? grid.downs : [0, 1000, 2500, 5000],
    rates: grid.rates?.length ? grid.rates : [5.99, 7.99, 9.99, 11.99],
  };
  const terms = live.terms;
  const cols = mode === "down" ? live.downs : live.rates;

  const startEdit = () => { setDraft({ terms: [...terms], downs: [...live.downs], rates: [...live.rates] }); setEditing(true); };
  const saveGrid = async () => {
    try {
      const { data } = await api.put("/desking/settings", { grid: { ...grid, ...draft, mode } });
      qc.setQueryData(["desk-settings"], data);
      setEditing(false);
      setDraft(null);
      toast.success("Payment grid saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the grid");
    }
  };
  const setDraftList = (key, arr) => setDraft((d) => ({ ...d, [key]: arr }));
  const colKey = mode === "down" ? "downs" : "rates";

  const patchFor = (term, col) =>
    mode === "down"
      ? { finance: { ...fin, term_months: term, cash_down: col } }
      : { finance: { ...fin, term_months: term, apr: col } };

  const isCurrent = (term, col) =>
    Number(fin.term_months) === term &&
    (mode === "down" ? Number(fin.cash_down) === Number(col) : Number(fin.apr) === Number(col));

  return (
    <Panel
      label="Payment grid" title={`${FREQ_TITLE[fin.frequency] || "Bi-weekly"} payments`}
      icon={Grid3x3} testid="desk-payment-grid"
      actions={
        <div className="flex gap-2 items-center">
          <Chip active={mode === "down"} onClick={() => setMode("down")} testid="grid-mode-down">by down</Chip>
          <Chip active={mode === "rate"} onClick={() => setMode("rate")} testid="grid-mode-rate">by rate</Chip>
          {editing ? (
            <Button size="sm" onClick={saveGrid} data-testid="grid-save"><Check className="h-3.5 w-3.5 mr-1" />Save grid</Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={startEdit} data-testid="grid-edit" title="Change the terms and columns"><Pencil className="h-3.5 w-3.5" /></Button>
          )}
        </div>
      }
      flush
    >
      {editing && (
        <div className="px-4 py-4 border-b border-border space-y-3" data-testid="grid-editor">
          <div>
            <div className="label-mono mb-2">Terms (rows)</div>
            <div className="flex gap-2 flex-wrap items-center">
              {draft.terms.map((t, i) => (
                <div key={i} className="relative w-20">
                  <Input type="number" value={t} onChange={(e) => setDraftList("terms", draft.terms.map((x, k) => (k === i ? Number(e.target.value) : x)))} className="h-9 text-right tnum" data-testid={`grid-edit-term-${i}`} />
                  <button onClick={() => setDraftList("terms", draft.terms.filter((_, k) => k !== i))} className="absolute -right-1 -top-1 w-4 h-4 rounded-full bg-card border border-border text-muted-foreground hover:text-red-400 flex items-center justify-center"><X className="h-2.5 w-2.5" /></button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setDraftList("terms", [...draft.terms, 60])} data-testid="grid-edit-add-term"><Plus className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          <div>
            <div className="label-mono mb-2">{mode === "down" ? "Down payment columns" : "Rate columns (%)"}</div>
            <div className="flex gap-2 flex-wrap items-center">
              {draft[colKey].map((c, i) => (
                <div key={i} className="relative w-24">
                  <Input type="number" step={mode === "down" ? 100 : 0.01} value={c}
                    onChange={(e) => setDraftList(colKey, draft[colKey].map((x, k) => (k === i ? Number(e.target.value) : x)))}
                    className="h-9 text-right tnum" data-testid={`grid-edit-col-${i}`} />
                  <button onClick={() => setDraftList(colKey, draft[colKey].filter((_, k) => k !== i))} className="absolute -right-1 -top-1 w-4 h-4 rounded-full bg-card border border-border text-muted-foreground hover:text-red-400 flex items-center justify-center"><X className="h-2.5 w-2.5" /></button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setDraftList(colKey, [...draft[colKey], mode === "down" ? 0 : 9.99])} data-testid="grid-edit-add-col"><Plus className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">Saving makes this the grid on every worksheet — same as Desking Settings.</p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="px-4 py-3 label-mono font-normal text-left">Term</th>
              {cols.map((c, i) => (
                <th key={`${c}-${i}`} className="px-5 py-3 label-mono font-normal text-right">
                  {mode === "down" ? money(c) : `${c}%`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Keep the 20px right inset in sync with the header th px-5 (td px-2 + button px-3). */}
            {terms.map((t) => (
              <tr key={t} className="border-b border-border/50 last:border-b-0">
                <td className="px-4 py-2.5 tnum text-muted-foreground">{t} mo</td>
                {cols.map((c, i) => {
                  const p = paymentFor(ws, settings, patchFor(t, c));
                  const cur = isCurrent(t, c);
                  return (
                    <td key={`${c}-${i}`} className="px-2 py-1.5 text-right">
                      <button
                        onClick={() => onApply(patchFor(t, c))}
                        data-testid={`grid-cell-${t}-${String(c).replace(".", "_")}`}
                        className={`w-full px-3 py-2 rounded-xl tnum text-right transition-colors ${
                          cur ? "bg-primary text-primary-foreground font-medium" : "hover:bg-white/[0.06] text-foreground"
                        }`}
                      >
                        {money(p)}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
